import type { PermissionMode } from '@features/clinic-access';

export interface RouteAccessPolicy {
  permissions: readonly string[];
  feature?: string;
  permissionMode?: PermissionMode;
}

export type ClinicGuardedRoutePath =
  | '/patients'
  | '/patients/:patientId'
  | '/visits/:visitId'
  | '/billing'
  | '/billing/new'
  | '/billing/quick'
  | '/billing/:invoiceId'
  | '/billing/:invoiceId/edit'
  | '/billing/:invoiceId/print'
  | '/billing/:invoiceId/payments/:paymentId/receipt'
  | '/staff'
  | '/staff/:membershipId'
  | '/roles'
  | '/roles/new'
  | '/roles/:roleId'
  | '/settings';

const patientView: RouteAccessPolicy = {
  permissions: ['patients.view'],
};
const invoiceView: RouteAccessPolicy = {
  permissions: ['billing.invoices.view'],
  feature: 'billing',
};
const invoiceCreate: RouteAccessPolicy = {
  permissions: ['billing.invoices.create'],
  feature: 'billing',
};
const rolesManage: RouteAccessPolicy = {
  permissions: ['staff.roles.manage'],
  feature: 'customRoles',
};

/** Canonical route authorization policies, shared by router and shell navigation. */
export const clinicRouteAccess: Record<ClinicGuardedRoutePath, RouteAccessPolicy> = {
  '/patients': patientView,
  '/patients/:patientId': patientView,
  '/visits/:visitId': {
    permissions: ['visits.view'],
  },
  '/billing': invoiceView,
  '/billing/new': invoiceCreate,
  '/billing/quick': {
    permissions: ['billing.invoices.create', 'billing.payments.collect'],
    feature: 'billing',
    permissionMode: 'all',
  },
  '/billing/:invoiceId': invoiceView,
  '/billing/:invoiceId/edit': invoiceCreate,
  '/billing/:invoiceId/print': invoiceView,
  '/billing/:invoiceId/payments/:paymentId/receipt': invoiceView,
  '/staff': {
    permissions: ['staff.manage', 'staff.invite'],
    feature: 'staffManagement',
  },
  '/staff/:membershipId': {
    permissions: ['staff.manage'],
    feature: 'staffManagement',
  },
  '/roles': rolesManage,
  '/roles/new': rolesManage,
  '/roles/:roleId': rolesManage,
  '/settings': {
    permissions: [
      'clinic.settings.manage',
      'clinic.services.manage',
      'clinic.rooms.manage',
      'clinic.availability.manage',
      'clinic.availability.own',
    ],
  },
};
